import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, PackageSearch, Truck, CircleDollarSign, Boxes } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Stat, EmptyState, Loader } from "@/components/kit";
import { ServiceCard } from "@/components/ServiceCard";
import { formatCOP } from "@/lib/format";

export default function BusinessDashboard() {
  const [all, setAll] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await rpc("get_business_services", { p_only_active: false });
      setAll(data || []);
      setErr(null);
    } catch (e) {
      setErr("No se pudieron cargar tus envíos. Revisa tu conexión e intenta de nuevo.");
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  if (all === null && !err) return <Loader />;

  const active = (all || []).filter((s) => !["DELIVERED", "CANCELLED"].includes(s.status));
  const delivered = (all || []).filter((s) => s.status === "DELIVERED");
  const spent = delivered.reduce((a, s) => a + Number(s.quoted_price), 0);

  return (
    <div>
      <PageHeader
        title="Panel del negocio"
        subtitle="Solicita y sigue tus envíos de carga en tiempo real."
        testId="business-dashboard"
        action={
          <Link
            to="/business/new"
            data-testid="dashboard-new-service-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#FF6A00] px-5 py-3 font-extrabold text-black shadow-md shadow-[#FF6A00]/25 transition-transform active:scale-95"
          >
            <PlusCircle size={18} /> Solicitar envío
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Envíos activos" value={active.length} accent="#FF6A00" testId="stat-active" />
        <Stat label="Entregados" value={delivered.length} testId="stat-delivered" />
        <Stat label="Total servicios" value={(all || []).length} testId="stat-total" />
        <Stat label="Gasto total" value={formatCOP(spent)} hint="Servicios entregados" testId="stat-spent" />
      </div>

      <h2 className="mb-4 mt-8 font-heading text-lg font-bold text-white">Envíos activos</h2>
      {err ? (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center" data-testid="dashboard-load-error">
          <p className="text-sm text-red-300">{err}</p>
          <button
            onClick={load}
            data-testid="dashboard-retry-button"
            className="mt-3 rounded-xl bg-[#FF6A00] px-5 py-2.5 text-sm font-extrabold text-black transition-transform active:scale-95"
          >
            Reintentar
          </button>
        </div>
      ) : active.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="No tienes envíos activos"
          subtitle="Crea tu primera solicitud de envío para conectar con un conductor disponible."
          testId="empty-active"
          action={
            <Link to="/business/new" className="rounded-xl bg-[#FF6A00] px-5 py-3 font-extrabold text-black">
              Solicitar envío
            </Link>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {active.map((s) => (
            <ServiceCard key={s.id} s={s} to={`/business/service/${s.id}`} testId={`active-service-${s.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}
